import React, { useEffect } from 'react';
import { X, Check, AlertTriangle } from 'lucide-react';
import { STATUS_STYLE } from './subscriptionConstants';
import { createPortal } from 'react-dom';
import {
  Modal as ChakraModal, ModalOverlay, ModalContent, ModalHeader,
  ModalCloseButton, ModalBody,
} from '@chakra-ui/react';

/* ────────────────────────────────────────────────────────────────
   TOKENS — clean white & purple ("purple.600") operations console.
   ──────────────────────────────────────────────────────────────── */
export const Tokens = () => (
  <style>{`
    :root {
      --paper: #FFFFFF;
      --panel: #FFFFFF;
      --panel-raised: #FFFFFF;
      --ink: #201A2E;
      --ink-dim: #6B6478;
      --ink-faint: #A79FBB;
      --rule: #E7E1F2;
      --brass: #9333EA;
      --brass-dim: #7E22CE;
      --sage: #16A34A;
      --rust: #DC2626;
      --slate: #8B8698;
    }
    .sa-root {
      font-family: 'SA-Body', system-ui, sans-serif;
      background: var(--paper);
      color: var(--ink);
      min-height: 100vh;
    }
    .sa-root, .sa-root * { box-sizing: border-box; }
    .sa-display { font-family: 'SA-Display', system-ui, sans-serif; letter-spacing: -0.01em; }
    .sa-mono { font-family: 'SA-Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; }

    .sa-scan {
      background-image: linear-gradient(180deg, rgba(147,51,234,0.012) 1px, transparent 1px);
      background-size: 100% 3px;
    }

    .sa-rail { background: #FAF7FE; border-right: 1px solid var(--rule); }
    .sa-navitem {
      display: flex; align-items: center; gap: 10px;
      color: var(--ink-dim); border-left: 2px solid transparent;
      transition: color .15s ease, background .15s ease, border-color .15s ease;
    }
    .sa-navitem:hover { color: var(--ink); background: rgba(147,51,234,0.06); }
    .sa-navitem.active { color: var(--brass); border-left-color: var(--brass); background: rgba(147,51,234,0.09); }

    .sa-panel { background: var(--panel); border: 1px solid var(--rule); border-radius: 10px; box-shadow: 0 1px 2px rgba(90,50,140,0.04); }
    .sa-panel-raised { background: var(--panel-raised); border: 1px solid var(--rule); border-radius: 10px; box-shadow: 0 8px 24px rgba(90,50,140,0.12); }

    .sa-btn {
      display: inline-flex; align-items: center; gap: 8px;
      border-radius: 7px; font-weight: 600; letter-spacing: .01em;
      transition: filter .15s ease, background .15s ease, border-color .15s ease;
      cursor: pointer; white-space: nowrap;
    }
    .sa-btn-primary { background: var(--brass); color: #FFFFFF; border: 1px solid var(--brass); }
    .sa-btn-primary:hover { filter: brightness(1.08); }
    .sa-btn-primary:disabled { opacity: .45; cursor: not-allowed; filter: none; }
    .sa-btn-ghost { background: transparent; color: var(--ink-dim); border: 1px solid var(--rule); }
    .sa-btn-ghost:hover { color: var(--ink); border-color: var(--ink-faint); }
    .sa-btn-danger { background: transparent; color: var(--rust); border: 1px solid rgba(220,38,38,0.35); }
    .sa-btn-danger:hover { background: rgba(220,38,38,0.08); }

    .sa-input {
      background: #FFFFFF; border: 1px solid var(--rule); color: var(--ink);
      border-radius: 6px; outline: none; transition: border-color .15s ease;
    }
    .sa-input:focus { border-color: var(--brass-dim); }
    .sa-input::placeholder { color: var(--ink-faint); }

    .sa-table-row { border-bottom: 1px solid var(--rule); transition: background .12s ease; }
    .sa-table-row:hover { background: rgba(147,51,234,0.035); cursor: pointer; }
    .sa-table-row:last-child { border-bottom: none; }

    .sa-stamp {
      display: inline-flex; align-items: center; gap: 6px;
      font-family: 'SA-Mono', ui-monospace, monospace;
      font-size: 10.5px; font-weight: 700; letter-spacing: 0.12em;
      padding: 3px 8px; border-radius: 4px; border: 1px solid;
      text-transform: uppercase; transform: rotate(-0.6deg);
    }
    .sa-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }
    .sa-pulse { animation: sa-pulse 2s ease-in-out infinite; }
    @keyframes sa-pulse { 0%,100% { opacity: 1; } 50% { opacity: .35; } }

    .sa-modal-backdrop { background: rgba(45,20,70,0.45); backdrop-filter: blur(2px); }
    .sa-tab { color: var(--ink-faint); border-bottom: 2px solid transparent; }
    .sa-tab.active { color: var(--brass); border-bottom-color: var(--brass); }
    .sa-tab:hover { color: var(--ink); }

    .sa-toast { background: var(--panel-raised); border: 1px solid var(--rule); border-left: 3px solid var(--brass); box-shadow: 0 8px 24px rgba(90,50,140,0.14); }
    .sa-toast.error { border-left-color: var(--rust); }

    .sa-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
    .sa-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .sa-scrollbar::-webkit-scrollbar-thumb { background: var(--rule); border-radius: 4px; }
  `}</style>
);

/* ────────────────────────────────────────────────────────────────
   STAMP — status pill, driven by subscriptionConstants.STATUS_STYLE
   ──────────────────────────────────────────────────────────────── */
export const Stamp = ({ status }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE.pending;
  return (
    <span className="sa-stamp" style={{ color: s.color, borderColor: s.color + '55', background: s.color + '14' }}>
      <span className={`sa-dot ${status === 'active' ? 'sa-pulse' : ''}`} style={{ background: s.color }} />
      {s.label}
    </span>
  );
};

/* ────────────────────────────────────────────────────────────────
   TOAST
   ──────────────────────────────────────────────────────────────── */
export function Toast({ toast, onDone }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDone, 3200);
    return () => clearTimeout(t);
  }, [toast, onDone]);
  if (!toast) return null;
  return (
    <div className={`sa-toast ${toast.type === 'error' ? 'error' : ''} fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg flex items-center gap-3 max-w-sm shadow-lg`}>
      {toast.type === 'error'
        ? <AlertTriangle size={16} color="#DC2626" />
        : <Check size={16} color="#16A34A" />}
      <span className="text-sm" style={{ color: 'var(--ink)' }}>{toast.message}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   MODAL
   ──────────────────────────────────────────────────────────────── */
export function Modal({ title, onClose, children, width = '32rem' }) {
  return (
    <ChakraModal isOpen onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(45,20,70,0.45)" backdropFilter="blur(2px)" />
      <ModalContent
        maxW={width}
        className="sa-panel-raised sa-scrollbar"
        borderRadius="10px"
        maxH="86vh"
      >
        <ModalHeader
          className="sa-display"
          fontSize="16px"
          fontWeight={600}
          color="var(--ink)"
          borderBottom="1px solid var(--rule)"
          py={4}
          px={5}
        >
          {title}
        </ModalHeader>
        <ModalCloseButton color="var(--ink-dim)" top="14px" right="18px" />
        <ModalBody p={5}>{children}</ModalBody>
      </ModalContent>
    </ChakraModal>
  );
}
/* ────────────────────────────────────────────────────────────────
   FIELD — labeled form row
   ──────────────────────────────────────────────────────────────── */
export function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '22px' }}>
      <span
        className="sa-mono"
        style={{
          fontSize: '11px',
          fontWeight: 600,
          color: 'var(--ink-dim)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   ROW — label/value line used in detail panels
   ──────────────────────────────────────────────────────────────── */
export const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-sm" style={{ color: 'var(--ink-faint)' }}>{label}</span>
    <span className={`text-sm ${mono ? 'sa-mono' : ''}`} style={{ color: 'var(--ink)' }}>{value}</span>
  </div>
);
